// OtpVerify.jsx — Sprint-84
// ─────────────────────────────────────────────────────────────────────
// 6-digit OTP screen used after step-1 login. Renders 6 single-digit
// inputs with auto-advance, paste support, resend countdown, and a
// back-link to retry the whole flow.
//
// Props:
//   loginToken    — short-lived JWT from POST /auth/login
//   emailMasked   — pre-masked email like "ad***@pickngo.com"
//   resendAfter   — initial cooldown seconds (default 60)
//   onSuccess(u)  — called with the user object after verify-otp returns
//   onCancel()    — back-to-login click
import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, ArrowLeft, ShieldCheck, RefreshCcw } from "lucide-react";
import useAuthStore from "../store/authStore";
import { toast } from "sonner";

export default function OtpVerify({ loginToken, emailMasked, resendAfter = 60, purpose = "login", onSuccess, onCancel }) {
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [cooldown, setCooldown] = useState(resendAfter || 60);
  const [resending, setResending] = useState(false);
  const inputsRef = useRef([]);
  const { verifyOtp, resendOtp, verifyRegistrationOtp, resendRegistrationOtp } = useAuthStore();
  // Sprint-87 — pick the right action based on `purpose`. Default to
  // login for backwards compatibility with existing call sites.
  const doVerify = purpose === "registration" ? verifyRegistrationOtp : verifyOtp;
  const doResend = purpose === "registration" ? resendRegistrationOtp : resendOtp;

  useEffect(() => { inputsRef.current[0]?.focus(); }, []);

  // 1-s ticker for the resend countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const otpString = useMemo(() => digits.join(""), [digits]);

  const setDigit = (idx, v) => {
    v = (v || "").replace(/\D/g, "").slice(0, 1);
    setDigits((d) => {
      const next = [...d];
      next[idx] = v;
      return next;
    });
    if (v && idx < 5) inputsRef.current[idx + 1]?.focus();
  };

  const onKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  const onPaste = (e) => {
    const t = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!t) return;
    e.preventDefault();
    const next = ["", "", "", "", "", ""];
    for (let i = 0; i < t.length; i++) next[i] = t[i];
    setDigits(next);
    inputsRef.current[Math.min(5, t.length)]?.focus();
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (otpString.length !== 6) { setError("Enter all 6 digits."); return; }
    setVerifying(true);
    setError(null);
    try {
      const u = await doVerify(loginToken, otpString);
      toast.success("Welcome back!");
      onSuccess?.(u);
    } catch (err) {
      setError(err?.message || "Invalid or expired code. Try again.");
      setDigits(["", "", "", "", "", ""]);
      inputsRef.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError(null);
    try {
      const res = await doResend(loginToken);
      if (res?.sent) {
        toast.success("New code sent");
        setCooldown(res.resend_after_s || 60);
      } else if (res?.error_code === "RESEND_COOLDOWN") {
        setCooldown(res.wait_seconds || 60);
        toast.message(`Please wait ${res.wait_seconds || 60}s before requesting another code.`);
      } else {
        toast.error("Couldn't resend right now. Try again in a moment.");
      }
    } catch {
      toast.error("Network error while resending. Try again.");
    } finally {
      setResending(false);
    }
  };

  // auto-submit when 6 digits typed
  useEffect(() => {
    if (otpString.length === 6 && !verifying) submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpString]);

  return (
    <form onSubmit={submit} className="w-full max-w-md animate-fadeInUp" data-testid="otp-verify-form">
      <div className="flex items-center gap-2 mb-6">
        <div className="w-10 h-10 bg-orange-50 border border-orange-200 rounded-xl flex items-center justify-center">
          <ShieldCheck className="h-5 w-5 text-orange-500" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900 leading-tight">Enter the 6-digit code</h1>
          <p className="text-xs text-slate-500" data-testid="otp-email-masked">Sent to <span className="font-mono">{emailMasked || "your email"}</span></p>
        </div>
      </div>

      <div className="flex gap-2 justify-between mb-3" onPaste={onPaste}>
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => (inputsRef.current[i] = el)}
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            maxLength={1}
            value={d}
            onChange={(e) => setDigit(i, e.target.value)}
            onKeyDown={(e) => onKeyDown(i, e)}
            className={`w-12 h-14 text-center text-2xl font-bold rounded-xl border focus:outline-none focus:ring-2 transition-all ${
              error ? "border-red-300 focus:border-red-400 focus:ring-red-100" : "border-slate-200 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20"
            }`}
            data-testid={`otp-digit-${i}`}
          />
        ))}
      </div>

      {error && (
        <p className="text-sm text-red-600 mb-3" data-testid="otp-error">{error}</p>
      )}

      <button
        type="submit"
        disabled={verifying || otpString.length !== 6}
        className="w-full h-11 bg-[#FF6B00] text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-[#E65A00] disabled:opacity-60 shadow-sm shadow-orange-500/20"
        data-testid="otp-verify-btn"
      >
        {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Sign In"}
      </button>

      <div className="mt-5 flex items-center justify-between text-xs">
        <button type="button" onClick={onCancel} className="text-slate-500 hover:text-slate-700 flex items-center gap-1" data-testid="otp-back-btn">
          <ArrowLeft className="h-3 w-3" /> Back to sign in
        </button>
        <button
          type="button"
          onClick={handleResend}
          disabled={cooldown > 0 || resending}
          className="text-orange-600 hover:text-orange-700 font-semibold flex items-center gap-1 disabled:text-slate-400 disabled:cursor-not-allowed"
          data-testid="otp-resend-btn"
        >
          <RefreshCcw className={`h-3 w-3 ${resending ? "animate-spin" : ""}`} />
          {cooldown > 0 ? `Resend in ${cooldown}s` : (resending ? "Sending..." : "Resend code")}
        </button>
      </div>
    </form>
  );
}
